/**
 * Rows service — operations on an owned row addressed by its OWN id.
 *
 * The planner (`records.update` → `applyRelationOps`) models a relation field
 * as a SET of targets and addresses removals by the target's id (a junction's
 * FK column). That covers field-shaped writes and nothing else. Three things
 * fall outside it, all of them real MCP tools:
 *
 *   - deleting rows by row id, with no record or field in hand
 *   - patching a row's own columns in place (a set-reconcile would delete and
 *     re-insert, minting a new row id and losing created_at)
 *   - the exactly-one-primary invariant, which spans sibling rows
 *
 * Keyed by relationship, exactly like `services/relations.ts:HANDLERS`. A
 * relationship whose table has no consumer simply has no entry for that verb,
 * and asking for it names the relationship rather than failing obscurely.
 */

import type { ResultOf, TypedDocumentNode } from "@graphql-typed-document-node/core"
import { EDITABLE_ROW_COLUMNS } from "../collections/records.js"
import { assertTimeValues } from "../collections/times.js"
import type { AugerField } from "../fields/fields.js"
import {
  deleteAttachmentsByIds,
  unsetOtherPrimaryAttachments,
  updateManyRecordAttachments,
  updateRecordAttachment,
} from "../gql/attachments.js"
import {
  deleteManyRecordContacts,
  unsetOtherPrimaryContacts,
  updateManyRecordContacts,
  updateRecordContact,
} from "../gql/contacts.js"
import { deleteLinksByIds } from "../gql/links.js"
import {
  deleteSourcesByIds,
  updateManyRecordSources,
  updateRecordSource,
} from "../gql/sources.js"
import { deleteTaggingsByIds } from "../gql/taggings.js"
import { deleteManyRecordTimes, updateManyRecordTimes, updateRecordTime } from "../gql/times.js"
import type { Mutate, Query } from "../index.js"

/** The six owned-row relationships. Same taxonomy as relations.ts. */
export type Relationship = "links" | "taggings" | "attachments" | "contacts" | "times" | "sources"

export interface RemoveRowsResult {
  removedIds: number[]
  /** Ids the delete matched no row for: never existed, already gone, or
   *  RLS-hidden. The three are indistinguishable without a follow-up SELECT. */
  missingIds: number[]
}

type DeleteDoc = TypedDocumentNode<unknown, { ids: number[] }>

const DELETE_DOCS: Record<Relationship, DeleteDoc> = {
  links: deleteLinksByIds as DeleteDoc,
  taggings: deleteTaggingsByIds as DeleteDoc,
  attachments: deleteAttachmentsByIds as DeleteDoc,
  contacts: deleteManyRecordContacts as DeleteDoc,
  times: deleteManyRecordTimes as DeleteDoc,
  sources: deleteSourcesByIds as DeleteDoc,
}

/** The single root field of a mutation result, whatever Hasura named it. */
function root(res: unknown): { returning?: { id: number }[] } | null {
  const value = Object.values((res ?? {}) as Record<string, unknown>)[0]
  return value && typeof value === "object" ? (value as { returning?: { id: number }[] }) : null
}

/** A patch on an owned row's own columns. Not an open bag — see UPDATE_HANDLERS.patchable. */
export type RowChanges = Record<string, unknown>

/** The row an in-place update returns, per relationship that has one: what its
 *  update document selects. */
export interface PatchedRowOf {
  contacts: NonNullable<ResultOf<typeof updateRecordContact>["update_record_contacts_by_pk"]>
  attachments: NonNullable<
    ResultOf<typeof updateRecordAttachment>["update_record_attachments_by_pk"]
  >
  sources: NonNullable<ResultOf<typeof updateRecordSource>["update_record_sources_by_pk"]>
  times: NonNullable<ResultOf<typeof updateRecordTime>["update_record_times_by_pk"]>
}

/** The relationships whose rows can be patched in place. */
export type PatchableRelationship = keyof PatchedRowOf

/** The relationships whose rows carry `is_primary`. */
export type PrimaryRelationship = "contacts" | "attachments"

export interface UpdateRowsResult<R extends PatchableRelationship = PatchableRelationship> {
  updated: PatchedRowOf[R][]
  missingIds: number[]
}

type UpdateOneDoc = TypedDocumentNode<unknown, { id: number; _set: RowChanges }>
type UpdateManyDoc = TypedDocumentNode<
  unknown,
  { updates: Array<{ where: { id: { _eq: number } }; _set: RowChanges }> }
>

interface UpdateHandler {
  one: UpdateOneDoc
  many: UpdateManyDoc
  patchable: readonly string[]
}

/**
 * Update handlers, and the closed set of columns each relationship accepts.
 *
 * Only the relationships with an in-place-update consumer are here. The
 * remaining four are removed and re-inserted through the planner like any
 * other field write; adding one is an entry plus a document.
 *
 * The column lists mirror what the MCP tools already accept. They exist so an
 * undeclared key fails by name here rather than reaching Hasura, where it is
 * either an opaque error or a write nothing reads back.
 */
const UPDATE_HANDLERS: Partial<Record<Relationship, UpdateHandler>> = {
  contacts: {
    one: updateRecordContact as UpdateOneDoc,
    many: updateManyRecordContacts as UpdateManyDoc,
    patchable: EDITABLE_ROW_COLUMNS.contacts,
  },
  attachments: {
    one: updateRecordAttachment as UpdateOneDoc,
    many: updateManyRecordAttachments as UpdateManyDoc,
    patchable: ["label", "is_primary", "position"],
  },
  // A source's identity is (source_type, source_id) and its stamp is derived,
  // so the one column worth patching in place is the cached `context` snapshot
  // a re-enrichment refreshes without minting a new row id.
  sources: {
    one: updateRecordSource as UpdateOneDoc,
    many: updateManyRecordSources as UpdateManyDoc,
    patchable: ["context"],
  },
  // record_id is deliberately absent: moving a time row to another record is
  // a field write on both records, not a column patch, so it goes through the
  // planner. schema_key/schema_id are absent for the same reason — the domain
  // derives the stamp from the resolved field.
  times: {
    one: updateRecordTime as UpdateOneDoc,
    many: updateManyRecordTimes as UpdateManyDoc,
    patchable: EDITABLE_ROW_COLUMNS.times,
  },
}

export interface SetPrimaryResult<R extends PrimaryRelationship = PrimaryRelationship> {
  row: PatchedRowOf[R]
  /** Set when the sibling unset failed. The target IS primary; stale
   *  duplicates may remain, and the caller can retry. */
  duplicatePrimariesPossible?: { reason: string }
}

type UnsetSiblingsDoc = TypedDocumentNode<unknown, { recordId: number; excludeId: number }>

/** The "unset every sibling" half of the exactly-one-primary invariant, per
 *  relationship. Only relationships whose rows carry `is_primary` are here —
 *  same closed-set discipline as UPDATE_HANDLERS above. */
const UNSET_SIBLING_DOCS: Partial<Record<Relationship, UnsetSiblingsDoc>> = {
  contacts: unsetOtherPrimaryContacts as UnsetSiblingsDoc,
  attachments: unsetOtherPrimaryAttachments as UnsetSiblingsDoc,
}

function assertPatchable(relationship: Relationship, changes: RowChanges): void {
  const handler = UPDATE_HANDLERS[relationship]
  if (!handler) throw new Error(`rows.update: no update handler for "${relationship}"`)
  for (const key of Object.keys(changes)) {
    if (!handler.patchable.includes(key)) {
      throw new Error(
        `rows.update: "${key}" is not patchable on ${relationship} — ` +
          `allowed columns are ${handler.patchable.join(", ")}. A field on the ` +
          `RECORD belongs in records.update(record_id, …) instead.`
      )
    }
  }
  if (relationship === "times") assertTimeValues(changes, "rows.update")
}

export function createRowsService({ mutate }: { query: Query; mutate: Mutate }) {
  const remove = async (
    relationship: Relationship,
    rawIds: number[]
  ): Promise<RemoveRowsResult> => {
    const doc = DELETE_DOCS[relationship]
    if (!doc) throw new Error(`rows.remove: no row handler for "${relationship}"`)
    if (!rawIds.length) return { removedIds: [], missingIds: [] }

    // Dedupe: a repeated id would otherwise appear twice in missingIds, which
    // is the one output not already deduped by passing through a Set.
    const ids = [...new Set(rawIds)]

    const res = await mutate(doc, { ids })
    const result = root(res)
    // A null mutation root is not "removed nothing" — it is Hasura declining
    // to answer. Reporting it as zero would look like a successful no-op.
    if (!result) throw new Error(`rows.remove: Hasura returned no result for "${relationship}"`)

    const removedIds = (result.returning ?? []).map((row) => row.id)
    const removed = new Set(removedIds)
    return { removedIds, missingIds: ids.filter((id) => !removed.has(id)) }
  }

  const update = async <R extends PatchableRelationship>(
    relationship: R,
    id: number,
    changes: RowChanges
  ): Promise<PatchedRowOf[R]> => {
    assertPatchable(relationship, changes)
    const handler = UPDATE_HANDLERS[relationship]!

    const res = await mutate(handler.one, { id, _set: changes })
    const updated = Object.values((res ?? {}) as Record<string, unknown>)[0]
    // update_*_by_pk returns null — not an error — when the row falls outside
    // the mutation's permission filter or was deleted since the caller read it.
    if (!updated) {
      throw new Error(
        `rows.update: ${relationship} row ${id} not found, or not permitted to ` +
          `write — the update matched no row`
      )
    }
    // The handler for R is R's update document, which selects PatchedRowOf[R].
    return updated as PatchedRowOf[R]
  }

  const updateMany = async <R extends PatchableRelationship>(
    relationship: R,
    rawUpdates: Array<{ id: number; changes: RowChanges }>
  ): Promise<UpdateRowsResult<R>> => {
    if (!rawUpdates.length) return { updated: [], missingIds: [] }

    // Two entries for the same id would be two independent _set statements,
    // and whichever Hasura applied second would silently drop the other's
    // columns. Merge per id (last write per key wins), keeping each id's FIRST
    // position in the input. Same rule as listings.updateMany.
    const order: number[] = []
    const mergedById = new Map<number, RowChanges>()
    for (const u of rawUpdates) {
      if (!mergedById.has(u.id)) order.push(u.id)
      mergedById.set(u.id, { ...(mergedById.get(u.id) ?? {}), ...u.changes })
    }

    for (const id of order) assertPatchable(relationship, mergedById.get(id)!)
    const handler = UPDATE_HANDLERS[relationship]!

    const res = await mutate(handler.many, {
      updates: order.map((id) => ({ where: { id: { _eq: id } }, _set: mergedById.get(id)! })),
    })
    const per = Object.values((res ?? {}) as Record<string, unknown>)[0] as
      | { returning?: PatchedRowOf[R][] }[]
      | null
    if (!per) throw new Error(`rows.updateMany: Hasura returned no result for "${relationship}"`)

    const updated: PatchedRowOf[R][] = []
    const missingIds: number[] = []
    order.forEach((id, i) => {
      const row = per[i]?.returning?.[0]
      if (row) updated.push(row)
      else missingIds.push(id)
    })
    return { updated, missingIds }
  }

  /**
   * Make one row the record's primary, unsetting its siblings.
   *
   * Two statements, not one transaction — Hasura only wraps root fields
   * sharing one document, and these need the row's record_id, which the first
   * statement returns. Order is deliberate: SET first, so the invariant "this
   * record has a primary" holds before anything can be lost. Unsetting first
   * would leave the record with NO primary if the second half failed.
   */
  const setPrimary = async <R extends PrimaryRelationship>(
    relationship: R,
    id: number
  ): Promise<SetPrimaryResult<R>> => {
    const unsetDoc = UNSET_SIBLING_DOCS[relationship]
    if (!unsetDoc) throw new Error(`rows.setPrimary: no primary handler for "${relationship}"`)

    const row = await update(relationship, id, { is_primary: true })

    const recordId = row.record_id
    if (typeof recordId !== "number") {
      return {
        row,
        duplicatePrimariesPossible: {
          reason:
            `the updated row carried no record_id, so siblings could not be ` +
            `identified — this row is primary, others may be too`,
        },
      }
    }

    try {
      const unsetRes = await mutate(unsetDoc, { recordId, excludeId: id })
      // A resolved-but-falsy root field is Hasura's shape for a
      // permission-filtered mutation — no error thrown, nothing cleared.
      // `affected_rows: 0` is a present root field (a real object) and means
      // the unset ran and cleared nothing, which is the correct, common
      // outcome for a record with only one row — not a failure.
      if (!root(unsetRes)) {
        return {
          row,
          duplicatePrimariesPossible: {
            reason:
              `the unset resolved without a result — could not verify other ` +
              `primaries were cleared; this row is primary, others may be too`,
          },
        }
      }
    } catch (e) {
      // Swallowing would report a clean success for a half-applied invariant;
      // rethrowing would discard the successful set. Report both facts.
      return {
        row,
        duplicatePrimariesPossible: {
          reason: e instanceof Error ? e.message : String(e),
        },
      }
    }
    return { row }
  }

  /**
   * Designate an existing row as `field`'s value, or pass `null` to drop the
   * designation. The row survives either way — an unqualified row still belongs
   * to the record's aggregate relation; only the field stops reading it.
   *
   * The stamp is derived from the field, never taken from the caller, which is
   * why `schema_key`/`schema_id` aren't patchable through `update`.
   */
  const qualify = async <R extends PatchableRelationship>(
    relationship: R,
    id: number,
    field: Pick<AugerField, "schema_key" | "schema_id"> | null
  ): Promise<PatchedRowOf[R]> => {
    const handler = UPDATE_HANDLERS[relationship]
    if (!handler) throw new Error(`rows.qualify: no update handler for "${relationship}"`)
    const schema_key = field?.schema_key ?? null
    const schema_id = field?.schema_id ?? null
    if (field && (schema_key == null || schema_id == null)) {
      throw new Error(
        `rows.qualify: the field carries no schema_key/schema_id stamp, so it ` +
          `can't own a ${relationship} row`
      )
    }
    const res = await mutate(handler.one, { id, _set: { schema_key, schema_id } })
    const updated = Object.values((res ?? {}) as Record<string, unknown>)[0]
    if (!updated) {
      throw new Error(
        `rows.qualify: ${relationship} row ${id} not found, or not permitted to ` +
          `write — the update matched no row`
      )
    }
    return updated as PatchedRowOf[R]
  }

  /** The columns `update`/`updateMany` accept for a relationship; empty when
   *  it has no update handler. Lets a caller route a patch it cannot express
   *  here (a re-anchor, an identity column) elsewhere instead of catching the
   *  refusal. */
  const patchable = (relationship: Relationship): readonly string[] =>
    UPDATE_HANDLERS[relationship]?.patchable ?? []

  return { remove, update, updateMany, setPrimary, qualify, patchable }
}
